import React, { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import reportService from "../services/reportService";
import equipeService from "../services/equipeService";
import { hideProcessing, showProcessing } from "../utils/utils";
import "./styles/relatorio.css";
import InputPeriodo from "./components/inputPeriodo";

let _relatorioInitialized = false;

export default function RelatorioAtendimentoEquipe() {
    const toast = useRef(null);
    const [equipesFiltro, setEquipesFiltro] = useState([]);
    const [equipeSelecionada, setEquipeSelecionada] = useState(null);
    const [inicio, setInicio] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [fim, setFim] = useState(new Date());
    const [relatorios, setRelatorios] = useState([]);
    const [loading, setLoading] = useState(false);

    // Carrega as equipes disponíveis
    useEffect(() => {
        carregarEquipes();
    }, []);

    // Busca o relatório quando o componente é inicializado
    useEffect(() => {
        if (!_relatorioInitialized) {
            _relatorioInitialized = true;
            return;
        }
        
        buscarRelatorio();
    }, []);

    const carregarEquipes = async () => {
        try {
            const response = await equipeService.getAll();
            const equipes = response.data || [];
            setEquipesFiltro(equipes.map(e => ({ label: e.nome, value: e.id })));
        } catch (error) {
            console.error("Erro ao carregar equipes:", error);
            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao carregar equipes"
            });
        }
    };

    const buscarRelatorio = async () => {
        showProcessing();
        setLoading(true);
        try {
            let dataInicio = null;
            let dataFim = null;

            // Busca com período apenas se não for "Todo o período"
            if (inicio && fim) {
                dataInicio = inicio.toISOString().split('T')[0];
                dataFim = fim.toISOString().split('T')[0];
            }

            const response = await reportService.getRelatorioAtendimentoEquipe(
                equipeSelecionada,
                dataInicio,
                dataFim
            );
            console.dir(response.data);
            setRelatorios(response.data || []);

        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao carregar relatório"
            });
        } finally {
            setLoading(false);
            hideProcessing();
        }
    };

    // Templates para formatação
    const statusOrdemBodyTemplate = (rowData) => {
        const statusMap = {
            'FINALIZADA': 'success',
            'CANCELADA': 'danger',
            'PAGAMENTO CONFIRMADO': 'info',
            'ORÇAMENTO': 'warning',
            'PARECER DE ACESSO': 'warning'
        };
        
        return <Tag value={rowData.statusOrdem} severity={statusMap[rowData.statusOrdem] || 'secondary'} />;
    };

    const statusAgendamentoBodyTemplate = (rowData) => {
        const statusMap = {
            'CONCLUÍDO': 'success',
            'CANCELADO': 'danger',
            'PENDENTE': 'warning',
            'CONFIRMADO': 'info'
        };
        
        return rowData.statusAgendamento ? 
            <Tag value={rowData.statusAgendamento} severity={statusMap[rowData.statusAgendamento] || 'secondary'} /> :
            <Tag value="Sem Agendamento" severity="secondary" />;
    };

    const dataBodyTemplate = (rowData, column) => {
        const data = rowData[column.field];
        return reportService.formatarData(data);
    };

    return (
        <div className="relatorio-container">
            <Toast ref={toast} />
            
            {/* Header */}
            <Card className="header-card">
                <div className="filtros-relatorio">
                    <div className="filtro-item">
                        <label style={{ display: "block", marginBottom: "5px" }}>Equipe</label>
                        <Dropdown
                            value={equipeSelecionada}
                            onChange={(e) => setEquipeSelecionada(e.value)}
                            options={equipesFiltro}
                            placeholder="Selecionar Equipe (Opcional)"
                            style={{ minWidth: '200px' }}
                            showClear
                        />
                    </div>

                    <div className="filtro-item">
                        <label style={{ display: "block", marginBottom: "5px" }}>Período</label>
                        <InputPeriodo
                            inicio={inicio}
                            fim={fim}
                            onChangePeriodo={(e) => {
                                setInicio(e.inicio);
                                setFim(e.fim);
                            }}
                        />
                    </div>

                    <Button 
                        label="Buscar" 
                        icon="pi pi-search" 
                        onClick={buscarRelatorio}
                        loading={loading}
                        className="button-confirm"
                    />
                </div>
            </Card>

            {/* Indicadores Principais */}
            <div className="indicadores-grid">
                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Total de Registros</div>
                        <div className="indicador-valor">{relatorios.length || 0}</div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Equipes Envolvidas</div>
                        <div className="indicador-valor">
                            {new Set(relatorios.map(r => r.equipeId)).size || 0}
                        </div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Ordens Únicas</div>
                        <div className="indicador-valor">
                            {new Set(relatorios.map(r => r.ordem)).size || 0}
                        </div>
                    </div>
                </Card>

                <Card className="indicador-card">
                    <div className="indicador-content">
                        <div className="indicador-label">Com Agendamento</div>
                        <div className="indicador-valor">
                            {relatorios.filter(r => r.dataCompromisso).length || 0}
                        </div>
                    </div>
                </Card>
            </div>

            {/* Tabela de Detalhes */}
            <Card className="section-card">
                <h3 style={{ marginTop: 0 }}>Atendimentos por Equipe</h3>
                <DataTable
                    value={relatorios}
                    loading={loading}
                    dataKey="ordem"
                    className="datatable-responsive"
                    responsiveLayout="scroll"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                    sortField="equipe"
                    sortOrder={1}
                >
                    <Column field="ordem" header="ID Ordem" sortable style={{ width: "80px" }} />
                    <Column 
                        field="equipe" 
                        header="Equipe" 
                        sortable
                        style={{ width: "150px" }}
                    />
                    <Column 
                        field="statusOrdem" 
                        header="Status Ordem" 
                        body={statusOrdemBodyTemplate}
                        sortable 
                        style={{ width: "130px" }}
                    />
                    <Column 
                        field="dataCompromisso" 
                        header="Data Compromisso" 
                        body={(rowData) => dataBodyTemplate(rowData, { field: 'dataCompromisso' })}
                        sortable
                        style={{ width: "140px" }}
                    />
                    <Column 
                        field="statusAgendamento" 
                        header="Status Agendamento" 
                        body={statusAgendamentoBodyTemplate}
                        sortable 
                        style={{ width: "150px" }}
                    />
                </DataTable>
            </Card>
        </div>
    );
}
